import { useState, useEffect, useCallback } from 'react';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isWithinInterval, parseISO, addWeeks, subWeeks } from 'date-fns';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export default function RoomCalendar() {
  const [currentWeekStart, setCurrentWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const response = await api.get('/calendar/bookings');
      setRooms(response.data.rooms);
      setBookings(response.data.bookings);
    } catch (error) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const weekDays = eachDayOfInterval({
    start: currentWeekStart,
    end: endOfWeek(currentWeekStart, { weekStartsOn: 1 })
  });

  const goToPreviousWeek = () => {
    setCurrentWeekStart(subWeeks(currentWeekStart, 1));
  };

  const goToNextWeek = () => {
    setCurrentWeekStart(addWeeks(currentWeekStart, 1));
  };

  const goToToday = () => {
    setCurrentWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  const getBookingForRoomAndDay = (roomId, day) => {
    return bookings.filter(booking => {
      const checkIn = parseISO(booking.check_in);
      const checkOut = parseISO(booking.check_out);
      return booking.room_id === roomId && isWithinInterval(day, { start: checkIn, end: addDays(checkOut, -1) });
    });
  };

  const isCheckInDay = (booking, day) => {
    return isSameDay(parseISO(booking.check_in), day);
  };

  const isCheckOutDay = (booking, day) => {
    return isSameDay(addDays(parseISO(booking.check_out), -1), day);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-blue-500';
      case 'checked_in': return 'bg-emerald-500';
      case 'checked_out': return 'bg-stone-400';
      default: return 'bg-stone-300';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div>
      </div>
    );
  }

  return (
    <div data-testid="room-calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Room Calendar</h1>
          <p className="text-stone-500 mt-1">Visual overview of room availability and bookings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={goToPreviousWeek}
            className="h-9 px-3 border-stone-300"
            data-testid="prev-week-btn"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToToday}
            className="h-9 px-4 border-stone-300"
            data-testid="today-btn"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={goToNextWeek}
            className="h-9 px-3 border-stone-300"
            data-testid="next-week-btn"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Week Display */}
      <div className="mb-4 text-center">
        <h2 className="text-lg font-semibold text-stone-800 font-['Manrope']">
          {format(currentWeekStart, 'MMMM d')} - {format(endOfWeek(currentWeekStart, { weekStartsOn: 1 }), 'MMMM d, yyyy')}
        </h2>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 mb-6 p-3 bg-white border border-stone-200 rounded-md">
        <span className="text-sm text-stone-600 font-medium">Status:</span>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500"></div>
          <span className="text-sm text-stone-600">Confirmed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-emerald-500"></div>
          <span className="text-sm text-stone-600">Checked In</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-stone-400"></div>
          <span className="text-sm text-stone-600">Checked Out</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-stone-200 border-2 border-stone-400"></div>
          <span className="text-sm text-stone-600">Available</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
        {rooms.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarDays className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p className="text-stone-500">No rooms available</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="text-left p-3 font-semibold text-stone-700 w-32 sticky left-0 bg-stone-50 z-10">Room</th>
                  {weekDays.map((day) => (
                    <th 
                      key={format(day, 'yyyy-MM-dd')} 
                      className={`text-center p-3 font-semibold text-stone-700 min-w-[100px] ${
                        isSameDay(day, new Date()) ? 'bg-sage-50' : ''
                      }`}
                    >
                      <div className="text-xs text-stone-500 uppercase">{format(day, 'EEE')}</div>
                      <div className={`text-lg ${isSameDay(day, new Date()) ? 'text-sage-700' : ''}`}>
                        {format(day, 'd')}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rooms.map((room, roomIndex) => (
                  <tr 
                    key={room.id} 
                    className="border-b border-stone-100 hover:bg-stone-50"
                    data-testid={`calendar-room-${room.id}`}
                  >
                    <td className="p-3 sticky left-0 bg-white z-10 border-r border-stone-100">
                      <div className="font-medium text-stone-900">Room {room.room_number}</div>
                      <div className="text-xs text-stone-500 capitalize">{room.room_type}</div>
                    </td>
                    {weekDays.map((day) => {
                      const dayBookings = getBookingForRoomAndDay(room.id, day);
                      const hasBooking = dayBookings.length > 0;
                      
                      return (
                        <td 
                          key={format(day, 'yyyy-MM-dd')} 
                          className={`p-1 text-center relative h-16 ${
                            isSameDay(day, new Date()) ? 'bg-sage-50/50' : ''
                          }`}
                        >
                          {hasBooking ? (
                            dayBookings.map((booking) => {
                              const isStart = isCheckInDay(booking, day);
                              const isEnd = isCheckOutDay(booking, day);
                              
                              return (
                                <div
                                  key={booking.id}
                                  className={`absolute inset-y-1 ${getStatusColor(booking.status)} ${
                                    isStart ? 'left-1 rounded-l-md' : 'left-0'
                                  } ${isEnd ? 'right-1 rounded-r-md' : 'right-0'} flex items-center justify-center`}
                                  title={`${booking.guest_name} - ${formatCurrency(booking.total_amount)}`}
                                >
                                  {isStart && (
                                    <span className="text-xs text-white font-medium px-1 truncate">
                                      {booking.guest_name.split(' ')[0]}
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <div className="w-2 h-2 rounded-full bg-stone-200 border border-stone-300"></div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <p className="text-sm text-stone-500">Total Rooms</p>
          <p className="text-2xl font-bold text-stone-900 font-['Manrope']">{rooms.length}</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <p className="text-sm text-stone-500">Currently Occupied</p>
          <p className="text-2xl font-bold text-emerald-700 font-['Manrope']">
            {rooms.filter(r => r.status === 'occupied').length}
          </p>
        </div>
        <div className="bg-white border border-stone-200 rounded-md p-4">
          <p className="text-sm text-stone-500">This Week's Bookings</p>
          <p className="text-2xl font-bold text-blue-700 font-['Manrope']">
            {bookings.filter(b => {
              const checkIn = parseISO(b.check_in);
              return isWithinInterval(checkIn, { start: currentWeekStart, end: endOfWeek(currentWeekStart, { weekStartsOn: 1 }) });
            }).length}
          </p>
        </div>
      </div>
    </div>
  );
}
